import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { RouteReuseStrategy, Router } from "@angular/router";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, tap } from "rxjs/operators";

import { RoutePath } from "../config/routing.config";
import { User } from "../models";
import { CustomRouteReuseStrategy } from "../shared/utils/route-reuse-strategy";
import { DigitalRoadworkDataService } from "./digital-roadwork-data.service";
import { DigitalRoadworkService } from "./digital-roadwork.service";

/** Possible responses after performing a user login. Maps to the translated error messages. */
export enum LoginResponse {
	Success = "",
	WrongCredentials = "error.wrong-credentials",
	TokenError = "error.token",
	ServerError = "error.server",
	NoOrgUnitError = "error.no-org-unit",
	UnknownError = "error.unknown",
}

/**
 * Provides functionality revolving around user authentication.
 */
@Injectable({
	providedIn: "root",
})
export class AuthService {
	private readonly userSubject = new BehaviorSubject<User | undefined>(undefined);
	public accessToken: string | null = null;
	public isInitialized = false;

	constructor(
		private readonly dataService: DigitalRoadworkDataService,
		private readonly digitalRoadworkService: DigitalRoadworkService,
		private readonly routeReuse: RouteReuseStrategy,
		private readonly router: Router,
	) {}

	/** Gets the current logged in user */
	public get User$(): Observable<User> {
		return this.userSubject.asObservable().pipe(
			filter((user) => user instanceof User),
			map((user) => user as User),
		);
	}

	/** Indicates wether a user is logged in or not. */
	public get IsUserLoggedIn$(): Observable<boolean> {
		return this.userSubject.asObservable().pipe(
			filter(() => this.isInitialized),
			map((user) => !!user),
			distinctUntilChanged(),
		);
	}

	public refreshToken(): Observable<User> {
		return this.dataService.refreshToken().pipe(
			tap((user) => {
				this.isInitialized = true;
				this.userSubject.next(user);
				this.accessToken = user.token;
			}),
		);
	}

	/**
	 * Performs user login and returns a particular response.
	 */
	public login(username: string, password: string): Observable<LoginResponse> {
		return this.dataService.postLogin(username, password).pipe(
			map((user) => {
				if (user.purviewNames.length == 0) {
					return LoginResponse.NoOrgUnitError;
				}
				this.userSubject.next(user);
				this.accessToken = user.token;
				this.digitalRoadworkService.initialize();
				return LoginResponse.Success;
			}),
			catchError((error: HttpErrorResponse) => {
				console.error("Login request failed", error);
				switch (error.status) {
					case 401:
						return of(LoginResponse.WrongCredentials);
					case 500:
						return of(LoginResponse.ServerError);
					default:
						return of(LoginResponse.UnknownError);
				}
			}),
		);
	}

	/**
	 * Performs user logout, clear reused routes and redirects to the login page.
	 */
	public logout(shouldReload = false): void {
		// destroy all cached components, except of the current component the app is displaying.
		(<CustomRouteReuseStrategy>this.routeReuse).clearHandlers();

		this.userSubject.next(undefined);

		// Redirect to login and destroy the one remaining component.
		// This is necessary because RouteReuseStrategy only caches components after a new navigation was triggered.
		this.router.navigate([`/${RoutePath.Login}`]).then(() => {
			(<CustomRouteReuseStrategy>this.routeReuse).clearHandlers();
			this.dataService.postLogout().subscribe(() => (this.accessToken = null));
		});

		if (shouldReload) {
			location.reload();
		}
	}
}
